import { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";

import { v4 as uuidv4 } from "uuid";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DataTable from "cxfcomponents/Tables/DataTable";
import Checkbox from "@mui/material/Checkbox";
import StatusBadge from "cxfcomponents/StatusBadge";

import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell";
import SoftBox from "components/SoftBox";

import { useAuth0 } from "@auth0/auth0-react";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { shareFlow } from "redux/actions/portal/flowsActionCreator";

function UsersModal2({
  openUsersModal,
  setOpenUsersModal,
  users,
  roles,
  selectedUserFlow,
  userFlows,
  titleName,
  shareList,
  setShareList,
  flowView,
}) {
  const { user } = useAuth0();
  const dispatch = useDispatch();
  const [toShareFlows, setToShareFlows] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const handleClose = () => {
    setOpenUsersModal(false);
    setSelectAllChecked(false);
    setToShareFlows([]);
  };

  const doesUserIdExist = (data, userIdToCheck) => {
    return !data.some((entry) => entry.user_id === userIdToCheck);
  };

  const handleSelectedUser = (userData, isChecked) => {
    const { id, name } = userData;
    if (userFlows) {
      if (isChecked === true) {
        const uuid = uuidv4();

        if (doesUserIdExist(toShareFlows, id)) {
          setToShareFlows((prevToShareFlows) => [
            ...prevToShareFlows,
            {
              id: uuid,
              flow_id: selectedUserFlow?.id,
              flow_name: selectedUserFlow?.name,
              user_id: id,
              user_name: name,
              access_type: "readOnly",
            },
          ]);
        }
      } else {
        if (selectAllChecked) {
          if (toShareFlows && toShareFlows.length <= 1) {
            setSelectAllChecked(false);
          }
        }
        setToShareFlows((prevToShareFlows) =>
          prevToShareFlows.filter(
            (item) => !(item.flow_id === selectedUserFlow?.id && item.user_id === id)
          )
        );
      }
    }
  };

  const isDisabled = (userId) => {
    if (toShareFlows?.length > 0) {
      return toShareFlows.find((item) => item.user_id === userId) &&
        toShareFlows.find((item) => item.flow_id === selectedUserFlow?.id)
        ? false
        : true;
    } else {
      return true;
    }
  };

  const isChecked = (userId) => {
    return toShareFlows?.some(
      ({ flow_id, user_id: flowUserId }) =>
        flow_id === selectedUserFlow?.id && flowUserId === userId
    );
  };

  const handleShareFlow = async () => {
    try {
      const response = shareFlow(user?.stripeId, toShareFlows, selectedUserFlow);
      response.responseObject = toShareFlows;
      dispatch(response);
      handleClose();

      if (toShareFlows.length > 0) {
        toast.success(`Flow ${selectedUserFlow.name} has been shared successfully!`);
      } else {
        toast.warning(`Unable to share the flow, No users selected`);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleAccessTypeChange = async (selectedOption, userId) => {
    const updatedUserFlows = toShareFlows?.map((toShareFlow) => {
      if (toShareFlow.flow_id === selectedUserFlow?.id && toShareFlow.user_id === userId) {
        return { ...toShareFlow, access_type: selectedOption };
      }
      return toShareFlow;
    });

    setToShareFlows(updatedUserFlows);
  };

  const toggleSelectAll = () => {
    const selected = !selectAllChecked;
    setSelectAllChecked(selected);

    for (const user of shareList) {
      if (user.auth0_id !== user.cxfUserId) {
        handleSelectedUser(
          { id: user.auth0_id, name: `${user.first_name} ${user.last_name}` },
          selected
        );
      }
    }
  };

  const dataTableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "share to",
          accessor: "selectUser",
          canGroupBy: false,
          disableSortBy: true,
          disableGlobalFilter: true,
          Cell: ({ value }) => {
            return (
              <Checkbox
                checked={isChecked(value.id)} // Step 4: Update checked state
                onChange={(event) => handleSelectedUser(value, event.target.checked)}
              />
            );
          },
        },
        {
          Header: "users",
          accessor: "users",
          width: 500,
          disableSortBy: false,
          canGroupBy: false,
          disableGlobalFilter: true,
          defaultSortOrder: "desc",
        },
        {
          Header: "name",
          accessor: "name",
          show: false,
        },
        {
          Header: "status",
          accessor: "status",
          Cell: ({ value }) => {
            return <StatusBadge status={value} />;
          },
          canGroupBy: false,
          disableGlobalFilter: true,
        },
        {
          Header: "access type",
          accessor: "accessType",
          canGroupBy: false,
          disableSortBy: true,
          disableGlobalFilter: true,
          Cell: ({ value }) => {
            const { id } = value;
            return (
              <SoftBox width="250px" className="inline-select-wrapper">
                <SoftSelect
                  key={id}
                  isDisabled={isDisabled(id)}
                  options={[
                    { value: "readOnly", label: "Read Only" },
                    { value: "editor", label: "Editor" },
                  ]}
                  defaultValue={{
                    value: isDisabled(id)
                      ? "readOnly"
                      : toShareFlows?.find((item) => item.user_id === id)?.access_type,
                    label: isDisabled(id)
                      ? "Select access type"
                      : toShareFlows?.find(
                          (item) => item.user_id === id && item.flow_id === selectedUserFlow?.id
                        )?.access_type === "readOnly"
                      ? "Read Only"
                      : toShareFlows?.find(
                          (item) => item.user_id === id && item.flow_id === selectedUserFlow?.id
                        )?.access_type === "editor"
                      ? "Editor"
                      : "Read Only",
                  }}
                  onChange={({ value: selectedOption }) => [
                    handleAccessTypeChange(selectedOption, id),
                  ]}
                />
              </SoftBox>
            );
          },
        },
      ],
      rows:
        users && shareList
          ? [
              ...shareList.map((item) => {
                return {
                  selectUser: { id: item.auth0_id, name: `${item.first_name} ${item.last_name}` },
                  users: (
                    <ProductCellfromUser
                      id={item.auth0_id}
                      image={item.image}
                      name={`${item.first_name} ${item.last_name}`}
                      firstName={item.first_name}
                      lastName={item.last_name}
                      email={item.email_address}
                      checked={true}
                    />
                  ),
                  name: `${item.first_name} ${item.last_name}`,
                  role: (() => {
                    if (roles?.length !== 0) {
                      if (!item.role_id) {
                        return "";
                      } else {
                        const roleName = roles?.find((role) => role.id === item.role_id)?.name;
                        return roleName ? roleName : "";
                      }
                    } else {
                      return "";
                    }
                  })(),
                  lastActive: "",
                  status: item?.status,
                  accessType: { id: item.auth0_id, name: `${item.first_name} ${item.last_name}` },
                };
              }),
            ]
          : [],
    };
  }, [users, roles, userFlows, selectedUserFlow, selectAllChecked, shareList, toShareFlows]);

  useEffect(() => {
    if (userFlows && selectedUserFlow) {
      const existingUserflows = userFlows?.filter((item) => item.flow_id === selectedUserFlow.id);
      if (existingUserflows.length === 0) {
        const userToShow = users?.filter(
          (item) => item.auth0_id != user?.cxfUserId && item.status === "active"
        );
        setShareList(userToShow);
      } else {
        const invitedUserIds = existingUserflows.map(
          (existingUserflow) => existingUserflow.user_id
        );

        const notInvitedUsers = users?.filter(
          (item) =>
            item.auth0_id !== selectedUserFlow.created_by_id &&
            !invitedUserIds.includes(item.auth0_id)
        );
        setShareList(notInvitedUsers);
      }
    }
  }, [userFlows, selectedUserFlow]);

  return (
    <Dialog
      fullWidth
      open={openUsersModal}
      onClose={handleClose}
      PaperProps={{
        style: {
          maxWidth: "1020px",
        },
      }}
    >
      <DialogContent className="share-flow-content-index-2">
        <DialogTitle sx={{ marginLeft: "8px" }}>
          {titleName} Flow: {selectedUserFlow?.name}
        </DialogTitle>
        <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "100px" }} sx={{ marginLeft: "24px" }}>
          <SoftTypography variant="h6" mb={1}>
            <Checkbox
              checked={selectAllChecked}
              onChange={toggleSelectAll}
              indeterminate={
                selectAllChecked && userFlows?.some((flow) => flow.access_type === "editor")
              }
            />
            Select All
          </SoftTypography>
        </SoftBox>
        <DataTable
          table={dataTableData}
          hiddenTopButtons={true}
          flowView={flowView}
          withSearchbar={true}
        />
      </DialogContent>
      <DialogActions>
        <SoftButton variant="outlined" color="info" onClick={handleClose}>
          Close
        </SoftButton>
        <SoftButton
          color="info"
          onClick={handleShareFlow}
          disabled={toShareFlows?.length > 0 ? false : true}
        >
          {titleName}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

export default UsersModal2;
